import util from 'preact-util';

export const DEFAULT_CALENDAR_COLORS = [
    '#3788d8', // Blue
    '#28a745', // Green
    '#dc3545', // Red
    '#fd7e14', // Orange
    '#6f42c1', // Purple
    '#20c997', // Teal
    '#e83e8c', // Pink
    '#17a2b8', // Cyan
];

export const COURSE_COLOR = '#6f42c1';

export function getCalendarColor(calendars, calendarId) {
    const calendarIndex = calendars.findIndex(c => c.calendarId === calendarId);
    const calendar = calendars[calendarIndex];
    if (calendar?.backgroundColor) {
        return calendar.backgroundColor;
    }
    return DEFAULT_CALENDAR_COLORS[calendarIndex % DEFAULT_CALENDAR_COLORS.length] || '#3788d8';
}

/** The club's main calendar («Klubbkalenderen», isMain) owns the courses; '' when there is none. */
export function courseCalendarId(calendars) {
    const main = (calendars || []).find(c => c && c.isMain);
    return main ? main.calendarId : '';
}

/** Courses take the main calendar's colour; purple without a main calendar. */
export function courseColor(calendars) {
    const id = courseCalendarId(calendars);
    return id ? getCalendarColor(calendars, id) : COURSE_COLOR;
}

/** Courses follow the main calendar's chip filter; always shown without a main calendar. */
export function courseVisible(calendars, selectedCalendars) {
    const selected = selectedCalendars || [];
    const id = courseCalendarId(calendars);
    return selected.length === 0 || !id || selected.includes(id);
}

export function getCalendarName(calendars, calendarId) {
    const calendar = calendars.find(c => c.calendarId === calendarId);
    return calendar?.name || '';
}

function expandCourses(courses) {
    const now = new Date();
    const items = [];
    courses.forEach(course => {
        const dates = course.dates || [];
        const participants = course.participants || [];
        const slots = course.slots || 0;
        dates.forEach((dateObj, dateIndex) => {
            if (dateObj.startTime && new Date(dateObj.startTime) >= now) {
                items.push({
                    isCourse: true,
                    courseId: course.id,
                    courseTitle: course.title,
                    dateMd5: dateObj.md5,
                    dateIndex,
                    start: dateObj.startTime,
                    end: dateObj.endTime,
                    title: dateObj.title || course.title,
                    location: dateObj.location || course.place,
                    description: dateObj.description || course.ingress,
                    participantsCount: participants.length,
                    slots,
                });
            }
        });
    });
    return items;
}

export async function loadPublicCalendarData(customerId, { eventsLimit = 100, coursesLimit = 100 } = {}) {
    if (!customerId) {
        return { events: [], courses: [], calendars: [], courseItems: [], error: 'No customer ID provided' };
    }

    try {
        const [eventsResponse, coursesResponse] = await Promise.all([
            util.fetchApi(`/api/calendarevents/public/${customerId}`, { method: 'GET' }, { limit: eventsLimit }),
            util.fetchApi('/api/courses/', { method: 'GET' }, {
                customer: customerId,
                limit: coursesLimit,
                sort: 'registrationStartDate',
            }),
        ]);

        if (!eventsResponse || eventsResponse.status !== 200) {
            return {
                events: [],
                courses: [],
                calendars: [],
                courseItems: [],
                error: eventsResponse?.message || 'Failed to load events',
            };
        }

        const events = eventsResponse.data || [];
        const calendars = eventsResponse.included?.calendars || [];
        const courses = (coursesResponse?.status === 200 && coursesResponse?.data) || [];
        const courseItems = expandCourses(courses);

        return { events, courses, calendars, courseItems, error: null };
    } catch (err) {
        return { events: [], courses: [], calendars: [], courseItems: [], error: 'Failed to load events' };
    }
}
